// Is this account in the new 챌린지 program? One reactive answer, shared by the
// tab bar (the 새 챌린지 tab) and the /today gate (PR 1a).
//
//   programSettings/global.mode   off (or no doc) → out · all → in · beta → in when programAccess/{uid} exists
//
// Read once per account per page load: the settings doc, and the access doc
// only in beta. Any error or a timeout is out (the program is opt-in). The
// answer is remembered on the device, so a program account sees its tab at
// once on the next load while the reads go again.
//
// The two reads are made here, not through programService: the tab bar loads
// this store with the app, and programService (the admin writes, the join,
// the event log) stays with the program's own pages.
import { reactive } from 'vue';
import { db } from '@/firebaseConfig';

const KEY = 'yp:program:';
const LOAD_MS = 6000;

const read = (c, id) => db.collection(c).doc(id).get().then((d) => (d.exists ? d.data() : null));

export const programStore = reactive({ uid: null, status: 'unknown', mode: 'off' }); // status: 'unknown' | 'in' | 'out'

let run = null; // { uid, p }: this page load's reads for this account
let gen = 0; // bumped by setProgramLocal, so a read still on its way never undoes it

export function programIn() { return programStore.status === 'in'; }

export function rememberedIn(uid) {
  if (!uid) return false;
  try { return window.localStorage.getItem(KEY + uid) === '1'; } catch (e) { return false; }
}

function remember(uid, on) {
  if (!uid) return;
  try {
    if (on) window.localStorage.setItem(KEY + uid, '1');
    else window.localStorage.removeItem(KEY + uid);
  } catch (e) { /* storage is off: the tab waits for the reads, as on a first visit */ }
}

async function lookup(uid) {
  const s = await read('programSettings', 'global');
  const mode = s && ['off', 'beta', 'all'].includes(s.mode) ? s.mode : 'off';
  if (mode === 'all') return { status: 'in', mode };
  if (mode === 'beta') return { status: (await read('programAccess', uid)) ? 'in' : 'out', mode };
  return { status: 'out', mode };
}

function within(p, ms) {
  let t;
  const late = new Promise((resolve, reject) => { t = setTimeout(() => reject(new Error('timeout')), ms); });
  return Promise.race([p, late]).then((v) => { clearTimeout(t); return v; }, (e) => { clearTimeout(t); throw e; });
}

async function settle(uid, g) {
  let next = { status: 'out', mode: 'off' };
  try { next = await within(lookup(uid), LOAD_MS); } catch (e) { /* any error or a timeout: out */ }
  if (programStore.uid !== uid || gen !== g) return; // another account since, or the admin page said so itself
  programStore.status = next.status;
  programStore.mode = next.mode;
  remember(uid, next.status === 'in');
}

/** Once per uid per page load; resolves the status ('in' | 'out'). Never rejects. */
export function loadProgram(uid) {
  if (!uid) return Promise.resolve('out');
  if (!run || run.uid !== uid) {
    programStore.uid = uid;
    programStore.status = rememberedIn(uid) ? 'in' : 'unknown';
    programStore.mode = 'off';
    run = { uid, p: settle(uid, gen) };
  }
  return run.p.then(() => (programStore.uid === uid ? programStore.status : 'out'));
}

/** loadProgram, but never waits longer than ms: 'out' then. */
export function ensureProgram(uid, ms = 6000) {
  let t;
  const late = new Promise((resolve) => { t = setTimeout(() => resolve('out'), ms); });
  return Promise.race([loadProgram(uid), late]).then((s) => { clearTimeout(t); return s; });
}

/** The admin page, after it changed the mode or the beta list in a way that moves its own account. */
export function setProgramLocal(status, mode) {
  gen += 1;
  if (status === 'in' || status === 'out') programStore.status = status;
  if (mode) programStore.mode = mode;
  remember(programStore.uid, programStore.status === 'in');
}

export default programStore;
